import React, {ReactElement, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import minus from '../../../utils/minus'

const TAG_VARIANTS = [
  'brand',
  'neutral',
  'neutralOutline',
  'success',
  'warning',
  'accent',
  'danger'
] as const

export type TagVariant = (typeof TAG_VARIANTS)[number]

const getTagColors = (variant: TagVariant) => cssTheme.colors.tag[variant]

export type TagSize = Extract<Size, 'xs' | 'sm' | 'md'>

interface Props {
  /** Content to be displayed in the Tag */
  children?: ReactNode
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** The button click handler */
  onClick?: () => void
  /** The size of the tag */
  size?: TagSize
  /** An Icon to display on the left side of the input. Inverted direction for RTL  */
  iconStart?: ReactElement
  /** An Icon to display on the right side of the input. Inverted direction for RTL */
  iconEnd?: ReactElement
  /** The variation of styling the component will have */
  variant?: TagVariant
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Extra element to be displayed on the right side (left if RTL) */
  annotation?: ReactNode
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledTag = styled(Tag)` */
  style?: React.CSSProperties
}

const AnnotationLabel = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  align-self: stretch;
  padding-top: ${cssTheme.layout.spacing.s200};
  padding-bottom: ${cssTheme.layout.spacing.s200};
  padding-right: ${cssTheme.layout.spacing.s300};
  padding-left: ${cssTheme.layout.spacing.s400};
  border-start-start-radius: 0;
  border-start-end-radius: ${cssTheme.layout.radius.md};
  border-end-end-radius: ${cssTheme.layout.radius.md};
  border-end-start-radius: 0;
  margin-left: ${minus(cssTheme.layout.spacing.s100)};

  transition:
    background-color 0.2s,
    color 0.2s;

  ${cssTheme.typography.titleXS};

  &:before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 0;
    left: 0;
    border-width: 12px 0 12px 5px;
    border-style: solid;
    transition: border-color 0.2s;
  }

  &.--size-xs {
    padding-top: ${cssTheme.layout.spacing.s100};
    padding-bottom: ${cssTheme.layout.spacing.s100};

    &:before {
      border-width: 10px 0 10px 5px;
    }
  }

  &.--size-md {
    ${cssTheme.typography.titleS};

    &:before {
      border-width: 16px 0 16px 8px;
    }
  }

  ${TAG_VARIANTS.map(variant => {
    const colors = getTagColors(variant)
    return `
    &.--variant-${variant} {
      color: ${colors.annotationContent};
      background-color: ${colors.annotationBackground};

      &:before {
        border-color: transparent;
        border-inline-start-color: ${colors.background};
      }
    }
    `
  }).join('')}
`

export const BasicTagElement = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: ${cssTheme.layout.radius.md};
  transition:
    color 0.2s,
    background-color 0.2s;

  ${TAG_VARIANTS.map(variant => {
    const colors = getTagColors(variant)
    return `
    &.--variant-${variant} {
      color: ${colors.content};
      background-color: ${colors.background};
      ${
        variant === 'neutralOutline'
          ? `outline: 1px solid ${cssTheme.colors.tag.neutralOutline.border};
             outline-offset: -1px;`
          : ''
      }
    }
  `
  }).join('')}

  ${cssTheme.typography.titleXS};
  padding: ${cssTheme.layout.spacing.s200};

  &.--size-xs {
    padding: ${cssTheme.layout.spacing.s100};
  }

  &.--size-md {
    ${cssTheme.typography.titleS}
    padding: ${cssTheme.layout.spacing.s250} ${cssTheme.layout.spacing.s200};
  }
`

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: ${cssTheme.layout.radius.md};

  ${BasicTagElement} {
    border-start-start-radius: ${cssTheme.layout.radius.md};
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-end-start-radius: ${cssTheme.layout.radius.md};
  }
`

const TextWrapper = styled.span`
  padding-left: ${cssTheme.layout.spacing.s200};
  padding-right: ${cssTheme.layout.spacing.s200};
`

const BasicTag = ({
  children,
  dataId,
  onClick,
  size = 'sm',
  iconStart,
  iconEnd,
  variant = 'brand',
  className,
  style
}: Props) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
  <BasicTagElement
    data-id={dataId}
    onClick={onClick}
    className={cx(`--size-${size}`, `--variant-${variant}`, className)}
    style={style}
  >
    {iconStart}

    {Boolean(children) && <TextWrapper>{children}</TextWrapper>}

    {iconEnd}
  </BasicTagElement>
)

export const Tag = ({
  annotation,
  variant = 'brand',
  size = 'sm',
  ...rest
}: Props) => {
  const hasAnnotation = Boolean(annotation)

  if (!hasAnnotation) {
    return <BasicTag {...rest} variant={variant} size={size} />
  }
  return (
    <Wrapper>
      <BasicTag {...rest} variant={variant} size={size} />
      <AnnotationLabel className={cx(`--variant-${variant}`, `--size-${size}`)}>
        {annotation}
      </AnnotationLabel>
    </Wrapper>
  )
}
